/*
 * Generated by tutor-mfe for all.
 *
 * Frontend-base counterpart of the legacy env.config.jsx.
 */


import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient, getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Alert,
  Button,
  Form,
  Icon,
  Spinner,
  StatefulButton,
} from '@openedx/paragon';
import { Nightlight, WbSunny } from '@openedx/paragon/icons';



import { DIRECT_PLUGIN, IFRAME_PLUGIN, PLUGIN_OPERATIONS } from '@openedx/frontend-plugin-framework';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }
  config.pluginSlots[slot_name].plugins.push(...plugins);
}

let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isThemeToggleEnabled = getConfig().DEMID_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #17171C;
            color: #D9E0EA;
          }
          a { color: #D9E0EA; }
          a:hover { color: #FFFFFF; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources.
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc.
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};



function setConfig() {
  const config = {
    pluginSlots: {},
  };

/**
 * Shared Demid app-shell contract.
 *
 * Existing MFE nodes are marked as shell regions so upstream apps do not need
 * forks. This effect only follows DOM structure; it never measures chrome.
 * CSS flex growth keeps short pages at viewport height and lets long pages
 * grow naturally.
 */
const BrandLayoutOverrides = () => {
  const patternUrl = `${getConfig().LMS_BASE_URL}/theming/asset/images/triangle-grid-outline.svg`;

  useEffect(() => {
    const root = document.querySelector('#root');
    if (!root) {
      return undefined;
    }

    const managedAttribute = 'data-demid-shell-managed';

    const mark = (node, attribute, value = '') => {
      if (!node) {
        return;
      }
      node.setAttribute(attribute, value);
      node.setAttribute(managedAttribute, '');
    };

    const commonAncestor = (nodes) => {
      if (!nodes.length) {
        return root;
      }
      const ancestors = [];
      let candidate = nodes[0];
      while (candidate) {
        ancestors.push(candidate);
        if (candidate === root) {
          break;
        }
        candidate = candidate.parentElement;
      }
      return ancestors.find(
        (node) => nodes.every((item) => node.contains(item)),
      ) || root;
    };

    const syncShellRegions = () => {
      root.querySelectorAll(`[${managedAttribute}]`).forEach((node) => {
        node.removeAttribute('data-demid-shell');
        node.removeAttribute('data-demid-shell-region');
        node.removeAttribute('data-demid-shell-body-chain');
        node.removeAttribute(managedAttribute);
      });

      const body = root.querySelector([
        '.demid-authn-shell__main',
        '#main',
        'main',
        '#dashboard-container',
        '.profile-page',
        '[role="main"]',
      ].join(', '));
      if (!body) {
        return;
      }

      const headers = [...root.querySelectorAll([
        'header.site-header-desktop',
        'header.site-header-mobile',
        'header.learning-header',
        'header.studio-header',
        'header.border-bottom',
        'header[class*="site-header"]',
        '.learner-variant-header',
      ].join(', '))].filter((node) => !body.contains(node));
      const footers = [...root.querySelectorAll('.wrapper-footer')];
      const admins = [
        ...root.querySelectorAll('[data-testid="instructor-toolbar"]'),
        ...[...root.querySelectorAll('.masquerade-bar')].map(
          (node) => node.closest('.shadow-sm') || node,
        ),
      ].filter((node, index, values) => values.indexOf(node) === index);

      const shell = root.querySelector('.demid-authn-shell')
        || commonAncestor([...headers, ...admins, body, ...footers]);

      mark(shell, 'data-demid-shell');
      headers.forEach((node) => mark(node, 'data-demid-shell-region', 'header'));
      admins.forEach((node) => mark(node, 'data-demid-shell-region', 'admin'));
      mark(body, 'data-demid-shell-region', 'body');
      footers.forEach((node) => mark(node, 'data-demid-shell-region', 'footer'));

      let chain = body.parentElement;
      while (chain && chain !== shell && shell.contains(chain)) {
        mark(chain, 'data-demid-shell-body-chain');
        chain = chain.parentElement;
      }
    };

    syncShellRegions();
    const mo = new MutationObserver(syncShellRegions);
    mo.observe(root, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      root.querySelectorAll(`[${managedAttribute}]`).forEach((node) => {
        node.removeAttribute('data-demid-shell');
        node.removeAttribute('data-demid-shell-region');
        node.removeAttribute('data-demid-shell-body-chain');
        node.removeAttribute(managedAttribute);
      });
    };
  }, []);

  return (
    <style>
      {`
      /*
       * Shell regions grow from their content. Free viewport space belongs to
       * BODY; no header/footer height is measured.
       */
      html,
      body,
      #root {
        min-height: 100vh;
        min-height: 100dvh;
      }
      body {
        margin: 0;
      }
      #root [data-demid-shell] {
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        min-height: 100dvh;
        width: 100%;
      }
      #root [data-demid-shell-body-chain] {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
        width: 100%;
      }
      #root [data-demid-shell-region="header"],
      #root [data-demid-shell-region="admin"],
      #root [data-demid-shell-region="footer"] {
        flex: 0 0 auto;
      }
      #root [data-demid-shell-region="header"] {
        align-items: center;
        min-height: 75px;
      }
      #root header[data-demid-shell-region="header"] {
        display: flex;
      }
      /*
       * frontend-base renders header.border-bottom > nav. Its nav must grow
       * explicitly when the outer header becomes flex for vertical centering.
       */
      #root header.border-bottom[data-demid-shell-region="header"] > nav {
        flex: 1 1 auto;
        width: 100%;
      }
      #root [data-demid-shell-region="header"] > .container,
      #root [data-demid-shell-region="header"] > .container-fluid,
      #root [data-demid-shell-region="header"] > .container-xl,
      #root header.learning-header .course-title-lockup,
      #root .learner-variant-header {
        align-items: center;
      }
      #root [data-demid-shell-region="header"] > .container,
      #root [data-demid-shell-region="header"] > .container-fluid,
      #root [data-demid-shell-region="header"] > .container-xl {
        display: flex;
        min-height: 75px;
        width: 100%;
      }
      #root header.site-header-desktop > .container-fluid > .nav-container {
        flex: 1 1 auto;
        width: 100%;
      }
      #root [data-demid-shell-region="body"] {
        flex: 1 1 auto;
        min-height: 0;
        width: 100%;
      }
      #root #dashboard-container #dashboard-content,
      [data-paragon-theme-variant="dark"] #root #dashboard-container #dashboard-content {
        min-height: 0 !important;
      }

      /* Shared shell gutters: site Header, LearningHeader, footers, content. */
      #root header.site-header-desktop .container-fluid,
      #root header.learning-header .container-xl,
      #root .learner-variant-header,
      #root footer.tutor-container,
      #root footer.footer .container-fluid,
      #root .footer .container-fluid,
      #root .pb-3.container,
      #root .pb-3.container-xl,
      #root main > .container,
      #root main > .container-xl,
      #root main .container-xl {
        max-width: 1600px !important;
        margin-left: auto;
        margin-right: auto;
        padding-left: 15px !important;
        padding-right: 15px !important;
      }

      /* Keep the LearningHeader logo consistent with the other MFE headers. */
      #root header.learning-header .logo img {
        height: 48px !important;
      }
      #root header.learning-header .course-info-header {
        padding-bottom: 0 !important;
        padding-top: 0 !important;
      }

      /* Match demidtheme LMS _footer.scss: same visible chrome as footer.html */
      #root .wrapper-footer {
        background-color: #fff;
        padding: 25px 0;
        border-top: 1px solid #d9dde1;
        isolation: isolate;
        overflow: hidden;
        position: relative;
        text-align: center;
      }
      #root .wrapper-footer::before {
        background-image: url("${patternUrl}");
        background-position: center top;
        background-size: 120px 104px;
        content: "";
        inset: 0;
        opacity: .07;
        pointer-events: none;
        position: absolute;
        z-index: 0;
      }
      #root .wrapper-footer > footer {
        position: relative;
        z-index: 1;
      }
      @media (min-width: 768px) {
        #root .wrapper-footer {
          padding: 45px 0;
        }
      }
      /* Match demidtheme LMS: powered-by, copyright-site, and .copyright hidden */
      #root footer.tutor-container .powered-area,
      #root footer.tutor-container .copyright-site,
      #root .wrapper-footer .copyright,
      #root footer.tutor-container .colophon {
        display: none !important;
      }
      #root footer.tutor-container .footer-top {
        margin: 0;
      }
      #root footer.tutor-container .footer-top::after {
        clear: both;
        display: block;
        content: "";
      }
      #root footer.tutor-container .nav-colophon {
        display: block;
        overflow: visible;
      }
      #root footer.tutor-container .nav-colophon ol {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        list-style: none;
        margin: 0;
        padding: 0;
      }
      @media (min-width: 992px) {
        #root footer.tutor-container {
          text-align: left;
        }
        #root footer.tutor-container .nav-colophon ol {
          justify-content: flex-end;
        }
      }
      #root footer.tutor-container .nav-colophon ol li {
        display: inline-block;
        vertical-align: top;
        padding: 0 8px;
        font-size: 14px;
        line-height: 20px;
      }
      @media (min-width: 768px) {
        #root footer.tutor-container .nav-colophon ol li {
          padding: 0 12px;
          font-size: 16px;
          line-height: 24px;
        }
      }
      #root footer.tutor-container .nav-colophon ol li a {
        color: #515661;
        text-decoration: none;
      }
      #root footer.tutor-container .nav-colophon ol li a:hover {
        color: #212529;
        text-decoration: none;
      }
      [data-paragon-theme-variant="dark"] #root .wrapper-footer {
        border-top-color: #33333d;
        background: #17171c;
      }
      [data-paragon-theme-variant="dark"] #root .wrapper-footer::before {
        filter: invert(1);
        opacity: .09;
      }
      [data-paragon-theme-variant="dark"] #root footer.tutor-container .nav-colophon ol li a {
        color: #d9e0ea;
      }
      [data-paragon-theme-variant="dark"] #root footer.tutor-container .nav-colophon ol li a:hover {
        color: #fff;
      }

      #root .shadow-sm:has(> .masquerade-bar) {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      #root .masquerade-bar {
        box-sizing: border-box;
        max-width: 1600px;
        margin-left: auto;
        margin-right: auto;
        padding-left: 15px;
        padding-right: 15px;
      }

      /* Authn keeps its banner layout, but its height comes from shell BODY. */
      #root .demid-authn-shell__main {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
      }
      #root .demid-authn-shell .layout,
      #root .demid-authn-shell .layout .bg-primary-400 .min-vh-100 {
        min-height: 0 !important;
      }
      /* The route layout must consume BODY's free space too. Keep its own
       * row/column direction and form spacing from the upstream Authn app. */
      #root .demid-authn-shell__main > .layout {
        flex: 1 0 auto;
      }
      #root .demid-authn-shell .layout .bg-primary-400 {
        background-color: #0082c7 !important;
        background-image:
          linear-gradient(rgba(0, 130, 199, .86), rgba(0, 130, 199, .86)),
          url("${patternUrl}");
        background-position: center;
        background-size: auto, 120px 104px;
      }
      #root .demid-authn-shell .full-vertical-height {
        height: auto !important;
        min-height: 0 !important;
      }
      `}
    </style>
  );
};



const DemidFooter = () => {
  const intl = useIntl();
  const config = getConfig();

  const demidFooterNavLinks = config.DEMID_FOOTER_NAV_LINKS || [];

  const messages = {
    "footer.poweredby.text": {
      id: "footer.poweredby.text",
      defaultMessage: "Работает на",
      description: "text for the footer",
    },
    "footer.tutorlogo.altText": {
      id: "footer.tutorlogo.altText",
      defaultMessage: "Runs on Tutor",
      description: "alt text for the footer tutor logo",
    },
    "footer.logo.altText": {
      id: "footer.logo.altText",
      defaultMessage: "Powered by Open edX",
      description: "alt text for the footer logo.",
    },
    "footer.copyright.text": {
      id: "footer.copyright.text",
      defaultMessage: `© ${new Date().getFullYear()}. Все права защищены.`,
      description: "copyright text for the footer",
    },
  };

  return (
    <div
      className="wrapper wrapper-footer"
      data-demid-shell-region="footer"
    >
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          {/* Kept in DOM for parity with LMS footer.html; hidden via BrandLayoutOverrides. */}
          <div className="powered-area">
            <ul className="logo-list">
              <li>{intl.formatMessage(messages["footer.poweredby.text"])}</li>
              <li>
                <a
                  href="https://edly.io/tutor/"
                  rel="noreferrer"
                  target="_blank"
                >
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/tutor-logo.png`}
                    alt={intl.formatMessage(
                      messages["footer.tutorlogo.altText"]
                    )}
                    width="57"
                  />
                </a>
              </li>
              <li>
                <a href="https://open.edx.org" rel="noreferrer" target="_blank">
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/openedx-logo.png`}
                    alt={intl.formatMessage(messages["footer.logo.altText"])}
                    width="79"
                  />
                </a>
              </li>
            </ul>
          </div>
          <nav className="nav-colophon" aria-label="About">
            <ol>
              {demidFooterNavLinks.map((link) => (
                <li key={link.url}>
                  <a
                    href={
                      link.url.startsWith("http")
                        ? link.url
                        : config.LMS_BASE_URL + link.url
                    }
                  >
                    {link.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <span className="copyright-site">
          {intl.formatMessage(messages["footer.copyright.text"])}
        </span>
        <div className="colophon">
          <p className="copyright">
            {intl.formatMessage(messages["footer.copyright.text"])}
          </p>
        </div>
      </footer>
    </div>
  );
};


/**
 * Modify Site Header main menus on account / profile MFEs.
 * Uses desktop_main_menu_slot / mobile_main_menu_slot (PLUGIN_OPERATIONS.Modify).
 */

const demidMyCoursesHref = () => {
  const config = getConfig();
  return `${config.LMS_BASE_URL}/dashboard`;
};

const demidCurrentPageHref = () => {
  if (typeof window !== 'undefined' && window.location?.pathname) {
    return `${window.location.origin}${window.location.pathname}`;
  }
  return '#';
};

const demidSetMainMenu = (widget, menu) => {
  if (!widget.content) {
    widget.content = {};
  }
  widget.content.menu = menu;
  return widget;
};

const demidModifyAccountMainMenu = (widget) => {
  const config = getConfig();
  return demidSetMainMenu(widget, [
    {
      type: 'item',
      href: demidMyCoursesHref(),
      content: 'Мои курсы',
      isActive: false,
    },
    {
      type: 'item',
      href: config.ACCOUNT_SETTINGS_URL || demidCurrentPageHref(),
      content: 'Учётная запись',
      isActive: true,
    },
  ]);
};

const demidModifyProfileMainMenu = (widget) => {
  return demidSetMainMenu(widget, [
    {
      type: 'item',
      href: demidMyCoursesHref(),
      content: 'Мои курсы',
      isActive: false,
    },
    {
      type: 'item',
      href: demidCurrentPageHref(),
      content: 'Профиль',
      isActive: true,
    },
  ]);
};



const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "Demid Online",
      description: "alt text for the mobile view header logo",
    },
  };

  const BASE_URL = config.LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Demid Online" className="logo">
        <img className="logo-image" src={`${BASE_URL}/theming/asset/images/logo.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
        <img className="logo-image logo-white" src={`${BASE_URL}/theming/asset/images/logo-white.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
      </a>
    </>
  );
};



const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Demid Online" className="logo">
        <img className="logo-image" src={`${BASE_URL}/theming/asset/images/logo.png`} alt="Demid Online" />
        <img className="logo-image logo-white" src={`${BASE_URL}/theming/asset/images/logo-white.png`} alt="Demid Online" />
      </a>
    </>
  );
};



const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookieNames = [
    'selected-paragon-theme-variant',
    'selected-theme-variant',
  ];
  const themeAttributeNames = [
    'data-paragon-theme-variant',
    'data-theme-variant',
  ];
  const primaryCookie = themeCookieNames[0];
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().DEMID_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const getThemeCookie = () => {
    for (const name of themeCookieNames) {
      const value = getCookie(name);
      if (value !== undefined) {
        return value;
      }
    }
    return undefined;
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const setThemeCookies = (value, opts) => {
    for (const name of themeCookieNames) {
      setCookie(name, value, opts);
    }
  };
  const setThemeAttribute = (theme) => {
    for (const attr of themeAttributeNames) {
      document.documentElement.setAttribute(attr, theme);
    }
  };

  const serverURL = new URL(getConfig().LMS_BASE_URL);

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: '/',
    expires: getCookieExpiry(),
  });

  const onToggleTheme = () => {
    let theme = '';

    if (getThemeCookie() === 'dark') {
      setThemeAttribute('light');
      setIsDarkThemeEnabled(false);
      theme = 'light';
    } else {
      setThemeAttribute('dark');
      setIsDarkThemeEnabled(true);
      theme = 'dark';
    }

    for (const name of themeCookieNames) {
        window.localStorage.setItem(name, theme);
    }
    setTimeout(() => {
      setThemeCookies(theme, getCookieOptions(serverURL));
      window.location.reload();
    }, 1);
  };

  useEffect(() => {
    const cookie = getThemeCookie();
    if (!cookie || cookie === 'undefined') {
      return;
    }
    if (cookie !== window.localStorage.getItem(primaryCookie)) {
      for (const name of themeCookieNames) {
          window.localStorage.setItem(name, cookie);
      }
      window.location.reload();
    }
    document.documentElement.setAttribute(themeAttributeNames[0], cookie);
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Переключить тему",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            defaultChecked={getThemeCookie() === "dark"}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${isDarkThemeEnabled ? "Light" : "Dark"
            } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};



/* Inlined into env.config.jsx — no import/export (see Imports.jsx). Avoid JSX `{{` (Tutor Jinja). */
const ScosLearnerNameFields = () => {
  const intl = useIntl();
  const user = getAuthenticatedUser();
  const username = user && user.username;

  const messages = {
    title: {
      id: 'demid.scos.learner.name.title',
      defaultMessage: 'ФИО для ГИС СЦОС',
      description: 'Section title for SCOS certificate name parts',
    },
    description: {
      id: 'demid.scos.learner.name.description',
      defaultMessage:
        'Эти поля нужны для отправки сертификата в портфолио ГИС СЦОС. Они не заменяют «Полное имя» в профиле Open edX.',
      description: 'Help text distinguishing SCOS FIO from Open edX name',
    },
    surname: {
      id: 'demid.scos.learner.name.surname',
      defaultMessage: 'Фамилия',
      description: 'Surname label',
    },
    givenName: {
      id: 'demid.scos.learner.name.given',
      defaultMessage: 'Имя',
      description: 'Given name label',
    },
    patronymic: {
      id: 'demid.scos.learner.name.patronymic',
      defaultMessage: 'Отчество (необязательно)',
      description: 'Patronymic label',
    },
    save: {
      id: 'demid.scos.learner.name.save',
      defaultMessage: 'Сохранить',
      description: 'Save button',
    },
    saved: {
      id: 'demid.scos.learner.name.saved',
      defaultMessage: 'Сохранено',
      description: 'Success state',
    },
    loadError: {
      id: 'demid.scos.learner.name.loadError',
      defaultMessage: 'Не удалось загрузить ФИО для СЦОС.',
      description: 'Load error',
    },
    saveError: {
      id: 'demid.scos.learner.name.saveError',
      defaultMessage: 'Не удалось сохранить. Проверьте фамилию и имя.',
      description: 'Save error',
    },
    required: {
      id: 'demid.scos.learner.name.required',
      defaultMessage: 'Фамилия и имя обязательны.',
      description: 'Client-side validation',
    },
  };

  const nameApiUrl = (uname) => {
    const base = (getConfig().LMS_BASE_URL || '').replace(/\/$/, '');
    return base + '/api/demidonline-scos/v1/learners/' + encodeURIComponent(uname) + '/name/';
  };

  const [surname, setSurname] = useState('');
  const [givenName, setGivenName] = useState('');
  const [patronymic, setPatronymic] = useState('');
  const [loading, setLoading] = useState(Boolean(username));
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveState, setSaveState] = useState('default');

  useEffect(() => {
    if (!username) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data } = await getAuthenticatedHttpClient().get(nameApiUrl(username));
        if (cancelled) return;
        setSurname(data.surname || '');
        setGivenName(data.name || '');
        setPatronymic(data.patronymic || '');
      } catch (err) {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        if (status === 404) {
          setSurname('');
          setGivenName('');
          setPatronymic('');
        } else {
          setLoadError(intl.formatMessage(messages.loadError));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username, intl]);

  const onSave = useCallback(async (event) => {
    event.preventDefault();
    if (!username) return;
    if (!surname.trim() || !givenName.trim()) {
      setSaveError(intl.formatMessage(messages.required));
      return;
    }
    setSaveError(null);
    setSaveState('pending');
    try {
      const body = {
        surname: surname.trim(),
        name: givenName.trim(),
        patronymic: patronymic.trim(),
      };
      const { data } = await getAuthenticatedHttpClient().put(nameApiUrl(username), body);
      setSurname(data.surname || '');
      setGivenName(data.name || '');
      setPatronymic(data.patronymic || '');
      setSaveState('complete');
      setTimeout(() => setSaveState('default'), 2000);
    } catch (err) {
      const apiMsg = err && err.response && err.response.data && err.response.data.message;
      setSaveError(apiMsg || intl.formatMessage(messages.saveError));
      setSaveState('default');
    }
  }, [username, surname, givenName, patronymic, intl]);

  if (!username) {
    return null;
  }

  const saveLabels = {
    default: intl.formatMessage(messages.save),
    pending: intl.formatMessage(messages.save),
    complete: intl.formatMessage(messages.saved),
  };

  return (
    <div className="account-section pt-3 mb-4" id="scos-learner-name" data-testid="scos-learner-name">
      <h2 className="section-heading h4 mb-3">{intl.formatMessage(messages.title)}</h2>
      <p className="text-muted mb-3">{intl.formatMessage(messages.description)}</p>
      {loading && (
        <div className="d-flex align-items-center mb-3">
          <Spinner animation="border" className="mr-2" screenReaderText="loading" />
        </div>
      )}
      {loadError && <Alert variant="danger">{loadError}</Alert>}
      {saveError && <Alert variant="danger">{saveError}</Alert>}
      {!loading && (
        <Form onSubmit={onSave}>
          <Form.Group>
            <Form.Label>{intl.formatMessage(messages.surname)}</Form.Label>
            <Form.Control
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              autoComplete="family-name"
              required
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>{intl.formatMessage(messages.givenName)}</Form.Label>
            <Form.Control
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              autoComplete="given-name"
              required
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>{intl.formatMessage(messages.patronymic)}</Form.Label>
            <Form.Control
              value={patronymic}
              onChange={(e) => setPatronymic(e.target.value)}
              autoComplete="additional-name"
            />
          </Form.Group>
          <StatefulButton
            type="submit"
            labels={saveLabels}
            state={saveState}
            variant="primary"
          />
        </Form>
      )}
    </div>
  );
};



/* Inlined into env.config.jsx — no import/export (see Imports.jsx). Avoid JSX `{{` (Tutor Jinja). */
const ScosEnrollmentWarning = () => {
  const intl = useIntl();
  const user = getAuthenticatedUser();
  const username = user && user.username;
  const [payload, setPayload] = useState(null);

  const messages = {
    title: {
      id: 'demid.scos.enroll.warn.title',
      defaultMessage: 'Результаты не попадут в ГИС СЦОС',
      description: 'Enrollment warning title without USIA',
    },
    body: {
      id: 'demid.scos.enroll.warn.body',
      defaultMessage:
        'Курс опубликован в ГИС СЦОС, но ваша учётная запись DemidOnline не привязана к ГИС. Обучение доступно, однако прогресс и сертификат не будут отправлены в портфолио, пока вы не сделаете привязку.',
      description: 'Enrollment warning body',
    },
    connect: {
      id: 'demid.scos.enroll.warn.connect',
      defaultMessage: 'Привязать ГИС СЦОС',
      description: 'CTA to link TPA',
    },
  };

  const courseIdFromLocation = () => {
    try {
      const path = window.location.pathname || '';
      const match = path.match(/\/courses\/([^/]+)\/about\/?$/);
      if (!match) return null;
      return decodeURIComponent(match[1]);
    } catch (e) {
      return null;
    }
  };

  const warningApiUrl = (courseId) => {
    const base = (getConfig().LMS_BASE_URL || '').replace(/\/$/, '');
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      base
      + '/api/demidonline-scos/v1/courses/'
      + encodeURIComponent(courseId)
      + '/enrollment-warning/?next='
      + next
    );
  };

  const absoluteLmsUrl = (pathOrUrl) => {
    if (!pathOrUrl) return '#';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const base = (getConfig().LMS_BASE_URL || '').replace(/\/$/, '');
    if (pathOrUrl.startsWith('/')) {
      return base + pathOrUrl;
    }
    return base + '/' + pathOrUrl;
  };

  useEffect(() => {
    if (!username) {
      setPayload(null);
      return undefined;
    }
    const courseId = courseIdFromLocation();
    if (!courseId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getAuthenticatedHttpClient().get(warningApiUrl(courseId));
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) setPayload(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  if (!(payload && payload.show_warning)) {
    return null;
  }

  return (
    <Alert variant="warning" className="mb-3" data-testid="scos-enrollment-warning">
      <Alert.Heading>{intl.formatMessage(messages.title)}</Alert.Heading>
      <p className="mb-2">{intl.formatMessage(messages.body)}</p>
      <Button as="a" href={absoluteLmsUrl(payload.connect_url)} variant="primary" size="sm">
        {intl.formatMessage(messages.connect)}
      </Button>
    </Alert>
  );
};



/* Inlined into env.config.jsx — no import/export (see Imports.jsx). Avoid JSX `{{` (Tutor Jinja). */
const ScosCertificateWarning = () => {
  const intl = useIntl();
  const user = getAuthenticatedUser();
  const username = user && user.username;
  const [payload, setPayload] = useState(null);

  const messages = {
    title: {
      id: 'demid.scos.cert.warn.title',
      defaultMessage: 'Сертификат не будет выпущен без ФИО для ГИС СЦОС',
      description: 'Certificate blocked without SCOS name title',
    },
    body: {
      id: 'demid.scos.cert.warn.body',
      defaultMessage:
        'Курс ожидается в портфолио ГИС СЦОС. Укажите Фамилию и Имя в профиле (раздел «ФИО для ГИС СЦОС»), затем запросите сертификат снова.',
      description: 'Certificate blocked without SCOS name body',
    },
    cta: {
      id: 'demid.scos.cert.warn.cta',
      defaultMessage: 'Указать ФИО в профиле',
      description: 'CTA to Account MFE name form',
    },
  };

  const courseIdFromLocation = () => {
    try {
      const path = window.location.pathname || '';
      // /learning/course/<courseId>/... or /learning/course/<courseId>/home
      const match = path.match(/\/learning\/course\/([^/]+)/);
      if (!match) return null;
      return decodeURIComponent(match[1]);
    } catch (e) {
      return null;
    }
  };

  const warningApiUrl = (courseId) => {
    const base = (getConfig().LMS_BASE_URL || '').replace(/\/$/, '');
    return (
      base
      + '/api/demidonline-scos/v1/courses/'
      + encodeURIComponent(courseId)
      + '/certificate-warning/'
    );
  };

  useEffect(() => {
    if (!username) {
      setPayload(null);
      return undefined;
    }
    const courseId = courseIdFromLocation();
    if (!courseId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getAuthenticatedHttpClient().get(warningApiUrl(courseId));
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) setPayload(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  if (!(payload && payload.show_warning)) {
    return null;
  }

  const accountHref = payload.account_url || ((getConfig().ACCOUNT_SETTINGS_URL || '/account/') + '#scos-learner-name');

  return (
    <Alert variant="warning" className="mb-3" data-testid="scos-certificate-warning">
      <Alert.Heading>{intl.formatMessage(messages.title)}</Alert.Heading>
      <p className="mb-2">{payload.message || intl.formatMessage(messages.body)}</p>
      <Button as="a" href={accountHref} variant="primary" size="sm">
        {intl.formatMessage(messages.cta)}
      </Button>
    </Alert>
  );
};



/* Inlined into env.config.jsx — no import/export (see Imports.jsx). Avoid JSX `{{` (Tutor Jinja). */
const ScosPublishedSidebarItem = () => {
  const intl = useIntl();
  const [published, setPublished] = useState(null);

  const messages = {
    label: {
      id: 'demid.scos.published.sidebar.label',
      defaultMessage: 'Published in GIS SCOS',
      description: 'Catalog about sidebar label when course is published in SCOS',
    },
    value: {
      id: 'demid.scos.published.sidebar.value',
      defaultMessage: 'Yes',
      description: 'Catalog about sidebar value for SCOS published status',
    },
  };

  const courseIdFromLocation = () => {
    try {
      const path = window.location.pathname || '';
      const match = path.match(/\/courses\/([^/]+)\/about\/?$/);
      if (!match) return null;
      return decodeURIComponent(match[1]);
    } catch (e) {
      return null;
    }
  };

  const publishedApiUrl = (courseId) => {
    const base = (getConfig().LMS_BASE_URL || '').replace(/\/$/, '');
    return (
      base
      + '/api/demidonline-scos/v1/courses/'
      + encodeURIComponent(courseId)
      + '/scos-published/'
    );
  };

  useEffect(() => {
    const courseId = courseIdFromLocation();
    if (!courseId) {
      setPublished(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getAuthenticatedHttpClient().get(publishedApiUrl(courseId));
        if (!cancelled) {
          setPublished(Boolean(data && data.scos_published));
        }
      } catch (err) {
        if (!cancelled) {
          setPublished(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!published) {
    return null;
  }

  return (
    <div data-testid="scos-published-sidebar-item">
      <div className="d-flex justify-content-between flex-wrap p-3">
        <span>{intl.formatMessage(messages.label)}</span>
        <span className="font-weight-bolder">{intl.formatMessage(messages.value)}</span>
      </div>
      <hr className="m-0" />
    </div>
  );
};


  addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'demid_brand_layout_overrides',
            type: DIRECT_PLUGIN,
            priority: 3,
            RenderWidget: BrandLayoutOverrides,
        },
    },
]);
  addPlugins(config, 'desktop_secondary_menu_slot', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'theme_switch_button',
            type: DIRECT_PLUGIN,
            RenderWidget: ToggleThemeButton,
        },
    },
]);
  addPlugins(config, 'mobile_header_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'theme_switch_button',
            type: DIRECT_PLUGIN,
            RenderWidget: MobileViewHeader,
        },
    },
]);
  addPlugins(config, 'logo_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'custom_logo',
            type: DIRECT_PLUGIN,
            RenderWidget: ThemedLogo,
        }
    }
]);
if (config.pluginSlots && config.pluginSlots['logo_slot']) {
  config.pluginSlots['logo_slot'].keepDefault = false;

  const logoInsertPlugins =
    config.pluginSlots['logo_slot'].plugins.filter(plugin => plugin.op === 'insert' && plugin.widget?.id === 'custom_logo');

  if (logoInsertPlugins.length > 1) {
    let kept = false;
    config.pluginSlots['logo_slot'].plugins =
      config.pluginSlots['logo_slot'].plugins.filter(plugin => {
        if (plugin.op === 'insert' && plugin.widget?.id === 'custom_logo') {
          if (kept) return false;
          kept = true;
        }
        return true;
      });
  }
}

if (config.pluginSlots && config.pluginSlots['org.openedx.frontend.layout.footer.v1']) {
  const demidFooterHelperIds = new Set([
    'demid_footer',
    'read_theme_cookie',
    'demid_brand_layout_overrides',
  ]);
  const footerPluginsToInsert =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => plugin.op === 'insert');
  const foreignFooterInserts = footerPluginsToInsert.filter(
    plugin => !demidFooterHelperIds.has(plugin.widget?.id)
  );

  // Allow a later plugin to replace demid_footer by inserting its own footer widgets.
  // Do not treat our theme/layout helpers as a competing footer.
  if (foreignFooterInserts.length > 0) {
  config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => {
      if (plugin.op === 'insert') {
        const widgetId = plugin.widget?.id;
        return widgetId !== 'demid_footer';
      }
      return true;
    });
  }
}

  return config;
}

export default setConfig;